import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import useApi from "../../hooks/useApi";
import {Button, SelectBox, SelectBoxRef} from "devextreme-react/select-box";
import IClient from "../../models/IClient";
import GestionClient from "../../pages/gestion-client/gestionclient";
import PopupPage from "../popups/PopupPage";
import {useQuery} from "@tanstack/react-query";
import Guid from "../../models/Guid";

;

interface IPopupGestionClientProps {
    onSelectionchanged?: (client: IClient) => void
    isModeSelection: boolean
}

function useClient(loadDataCurrentClient?: boolean, currentIdClient?: Guid) {
    //const [client, setClient, isExpired] = useStorage<IClient[]>({key: "lookup-client", expirationInMilliseconds: 3000});
    const {getData: getClient} = useApi<IClient[]>(`Client/lookup`);
    const {data: client, refetch} = useQuery({
        queryKey: [`lookup-client-${currentIdClient}`], queryFn: () => getClient({currentValueFieldName: "idClient", currentValue: currentIdClient})
    });
    const {getData: getCurrentClient} = useApi<IClient>('Client');
    const [currentSelectedItem, setCurrentSelectedItem] = useState<IClient>();
    const [isPopupGestionClientOpen, setIsPopupGestionClientOpen] = useState(false);
    const onPopupClose = useCallback(() => setIsPopupGestionClientOpen(false), [setIsPopupGestionClientOpen]);
    const selectBoxRef = useRef<SelectBoxRef>(null);

    const editorOptions = useMemo(() => {
        return {
            searchEnabled: true,
            dataSource: client,
            displayExpr: (data: any) => data ? `${data.nom} ${data.prenom}` : '',
            displayValue: "idClient",
            valueExpr: "idClient",
            openOnFieldClick: false,
            buttons: [{
                name: "find",
                options: {
                    onClick: () => setIsPopupGestionClientOpen(true),
                    stylingMode: "text",
                    disabled: false,
                    icon: "find"
                }
            }],
            onValueChanged: (e: any) => {
                if (!loadDataCurrentClient) return;
                getCurrentClient({customPath: `client/${e.value}`}).then((data) => {
                    setCurrentSelectedItem(data)
                });
            },
        }
    }, [client, loadDataCurrentClient, getCurrentClient, setCurrentSelectedItem])

    const PopupGestionClient = useCallback(({onSelectionchanged, isModeSelection}: IPopupGestionClientProps) => {
            const OnSelection = async (clientSelected?: IClient) => {
                if (clientSelected && !client?.find(f => f.idClient?.equals(clientSelected?.idClient))) {
                    client?.push(clientSelected);
                }

                if (isModeSelection) {
                    setCurrentSelectedItem(clientSelected);
                    if (clientSelected && onSelectionchanged)
                        onSelectionchanged(clientSelected);
                }
                setIsPopupGestionClientOpen(false);
            }

            if (isPopupGestionClientOpen) {
                const refClient = currentSelectedItem;
                const defaultSearchFromRef = !refClient ? '' : `${refClient.prenom ?? ''} ${refClient.nom ?? ''}`.trim();
                return (
                    <PopupPage<IClient> titre={"Sélection d'un client"} isPopupOpen={isPopupGestionClientOpen} showOk={true}
                                        isModeModifier={true} onClose={onPopupClose} width={"95vw"} height={"95vh"}
                                        mode={isModeSelection ? "liste" : "ajout"} onSelection={OnSelection}>
                        <GestionClient isModeSelection={true} defaultSearch={defaultSearchFromRef}/>
                    </PopupPage>
                )
            }
            return null;
        }, [isPopupGestionClientOpen, currentSelectedItem, onPopupClose, setIsPopupGestionClientOpen, setCurrentSelectedItem, refetch]
    )

    const ClientSelectBox = useCallback((params: any) => {
        const {dataField, component, column, data} = params;
        if (!component) return null;
        const formOptions = component.option();
        const value = dataField || !data ? formOptions.formData[dataField] : data[column.dataField];

        const onValueChanged = (args: any) => {
            component.updateData(dataField, args.value);
        }

        const onSelectionChanged = (e: any) => {
            setCurrentSelectedItem(e.selectedItem);
        }

        return (
            <SelectBox ref={selectBoxRef} readOnly={!params.isEditing || formOptions.readOnly}
                       stylingMode={dataField || !data ? "filled" : "underlined"}
                       onSelectionChanged={onSelectionChanged}
                       dataSource={editorOptions.dataSource}
                       displayExpr={editorOptions.displayExpr}
                       displayValue={editorOptions.displayValue}
                       valueExpr={editorOptions.valueExpr}
                       value={value}
                       onValueChanged={onValueChanged} openOnFieldClick={false}>
                <Button name={"find"} options={{
                    onClick: () => setIsPopupGestionClientOpen(true),
                    stylingMode: "text",
                    disabled: false,
                    icon: "find"
                }}/>
            </SelectBox>
        )
    }, [editorOptions.dataSource, editorOptions.displayExpr, editorOptions.displayValue, editorOptions.valueExpr]);

    return {
        clientEditorOptions: editorOptions,
        client: client,
        currentClient: currentSelectedItem,
        refreshData: () => refetch(),
        ClientSelectBox,
        PopupGestionClient,
        setIsPopupGestionClientOpen,
        isPopupGestionClientOpen
    }
}

export default useClient;